import { type FormEvent, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { ArticlePicker, TextWithArticleLinks } from '../knowledge/ArticleLinks';
import type { ArticleSummary } from '../knowledge/data';
import { formatDateTime, type TicketDetail } from './data';
import { useAction } from './useAction';

/** Hilo del ticket (guía 7.1): respuestas públicas y, solo para IT, notas internas. */
export function Conversation({ ticket }: { ticket: TicketDetail }) {
  return (
    <Card title={t('tk.hilo')}>
      {ticket.messages.length === 0 ? (
        <p className="text-sm text-slate-500">{t('tk.hilo_vacio')}</p>
      ) : (
        <ol className="space-y-3" aria-label={t('tk.hilo')}>
          {ticket.messages.map((m) => (
            <li key={m.id} className={`rounded-md border p-3 ${m.isPublic ? 'border-slate-200' : 'border-amber-200 bg-amber-50'}`}>
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span className="font-medium text-slate-800">{m.author ?? t('tk.autor_sistema')}</span>
                {m.authorId === ticket.requesterId && <Badge>{t('tk.solicitante')}</Badge>}
                <span>{formatDateTime(m.createdAt)}</span>
                {!m.isPublic && <Badge tone="amber">{t('tk.nota_interna')}</Badge>}
              </div>
              <p className="whitespace-pre-wrap text-sm text-slate-800">
                <TextWithArticleLinks text={m.body} />
              </p>
            </li>
          ))}
        </ol>
      )}
      {ticket.can.reply ? <Composer ticket={ticket} /> : ticket.statusCategory === 'cancelado' && <p className="mt-3 text-xs text-slate-500">{t('tk.hilo_cerrado')}</p>}
    </Card>
  );
}

function Composer({ ticket }: { ticket: TicketDetail }) {
  const me = useMe();
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [picking, setPicking] = useState(false);
  // Artículos insertados en esta respuesta: al enviarla quedan enlazados al ticket (los que sigan en el texto).
  const [picked, setPicked] = useState<ArticleSummary[]>([]);
  const action = useAction();
  const manager = ticket.can.manage;
  // La primera respuesta pública de IT a otra persona arranca el reloj del SLA: conviene saberlo antes de enviar.
  const startsSla = manager && !internal && !ticket.firstResponseAt && ticket.requesterId !== me.id;

  const insert = (a: ArticleSummary) => {
    setBody((b) => `${b}${b && !b.endsWith('\n') ? '\n' : ''}${a.code} · ${a.title}\n`);
    setPicked((list) => (list.some((p) => p.id === a.id) ? list : [...list, a]));
    setPicking(false);
    document.getElementById('tk-mensaje')?.focus();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void action.run(
      async () => {
        const text = body.trim();
        const r = await api<{ firstResponse: boolean }>(`/api/tickets/${ticket.number}/mensajes`, { json: { body: text, isPublic: !internal } });
        for (const a of picked.filter((p) => text.includes(p.code))) {
          await api(`/api/tickets/${ticket.number}/articulos`, { json: { articleId: a.id } });
        }
        setBody('');
        setInternal(false);
        setPicked([]);
        return r;
      },
      (r) => ((r as { firstResponse: boolean }).firstResponse ? t('tk.enviado_sla') : internal ? t('tk.nota_guardada') : t('tk.enviado')),
    );
  };

  return (
    <form onSubmit={submit} className="mt-4 space-y-2 border-t border-slate-100 pt-4">
      {action.error && <Alert>{action.error}</Alert>}
      {action.notice && <Alert tone="success">{action.notice}</Alert>}
      <Textarea
        id="tk-mensaje"
        label={internal ? t('tk.escribir_nota') : t('tk.escribir_respuesta')}
        rows={4}
        maxLength={10_000}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className={`block w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${internal ? 'border-amber-300 bg-amber-50 focus:ring-amber-300' : 'border-slate-300 focus:border-marca focus:ring-marca/30'}`}
      />
      {picking && <ArticlePicker initial={ticket.title} onPick={insert} onClose={() => setPicking(false)} />}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {manager ? (
          <div className="flex flex-wrap items-center gap-4">
            <Checkbox label={t('tk.nota_interna_check')} checked={internal} onChange={(e) => setInternal(e.target.checked)} />
            {!picking && (
              <Button variant="link" onClick={() => setPicking(true)}>
                {t('kb.insertar')}
              </Button>
            )}
          </div>
        ) : (
          <span />
        )}
        <Button type="submit" disabled={action.busy || !body.trim()}>
          {internal ? t('tk.guardar_nota') : t('tk.enviar_respuesta')}
        </Button>
      </div>
      {startsSla && <p className="text-xs text-slate-500">{t('tk.arranca_sla')}</p>}
      {!manager && <p className="text-xs text-slate-500">{t('tk.aviso_it')}</p>}
    </form>
  );
}
